import {
 ApplicationConfig, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection
} from '@angular/core';
import { provideHttpClient, HttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withPreloading, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '@Env';
import { FsAngularModule, FsCryptService, FsKeyStoreService } from '@fairandsmart/angular';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { provideNgxStripe } from 'ngx-stripe';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideToastr, ToastContainerDirective } from 'ngx-toastr';
import { provideQuillConfig } from 'ngx-quill';
import { MomentModule } from 'ngx-moment';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { FrenchDateAdapter } from '@Utils/french-date.adapter';
import { provideNativeDateAdapter } from '@angular/material/core';
import { FsOrgMaterialModule } from 'src/theme/material.module';
import { FsFormsModule } from '@fairandsmart/forms';
import { RegistryPropertyType } from '@fairandsmart/types';
import { provideCodeEditor } from '@ngstack/code-editor';
import { FsAngularMaterialComponentsModule } from '@fairandsmart/angular/components/material';
import { CustomPreloadingStrategy } from './custom-preloading-strategy.service';
import { DynamicLocaleId } from './dynamicLocaleId';
import { ROUTES } from './app.routes';
import version from '../assets/data/version.json';
import { provideKeycloakAngular } from './keycloak-config';
import { RequestService } from './components/organisation/requests/request.service';
import { provideNewsFeature } from './components/organisation/news/news-provider';
import { provideTaskFeature } from './components/organisation/task/task-provider';
import { provideOrganisationFeature } from './components/organisation/organisation-provider';
import { provideConfigurationFeature } from './components/organisation/configuration/configuration-provider';
import { ProcessingService } from './components/organisation/consents/processing.service';
import { BackgroundService } from './workers/worker.service';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${version.version}`);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideKeycloakAngular(),
    provideRouter(
      ROUTES,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withPreloading(CustomPreloadingStrategy)
    ),
    provideHttpClient(withInterceptors([includeBearerTokenInterceptor])),
    provideAnimations(),
    provideQuillConfig({}),
    provideToastr(),
    provideNgxStripe(environment.stripePublishableKey),
    provideStore(),
    provideEffects(),

    provideNativeDateAdapter(),
    FrenchDateAdapter,
    RequestService,
    ProcessingService,
    FsCryptService,
    FsKeyStoreService,
    BackgroundService,
    provideNewsFeature(),
    provideTaskFeature(),
    provideOrganisationFeature(),
    provideConfigurationFeature(),
    provideCodeEditor({}),
    importProvidersFrom(
      FsOrgMaterialModule,
      FsAngularMaterialComponentsModule,
      MomentModule.forRoot(),
      FsAngularModule.forRoot({
        apiUrl: environment.apiUrl,
        wsUrl: environment.wsUrl,
        catalogUrl: environment.catalogUrl,
        hubUrl: environment.hubUrl,
      }),
      FsFormsModule.forRoot({
        types: [
          { name: RegistryPropertyType.STRING, extends: 'input' },
          { name: RegistryPropertyType.DATE, extends: 'input' },
          { name: RegistryPropertyType.CHOICE, extends: 'select' },
          { name: RegistryPropertyType.MULTIPLE, extends: 'select', defaultOptions: { templateOptions: { multiple: true } } },
        ],
      }),
      ToastContainerDirective,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
      !environment.production ? StoreDevtoolsModule.instrument() : [],
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    CustomPreloadingStrategy,
    {
      provide: LOCALE_ID,
      useClass: DynamicLocaleId,
      deps: [TranslateService]
    }
  ]
};
